import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

/**
 * Full-screen generative light field behind every screen.
 * WebGL fragment shader (domain-warped fbm) in cobalt / violet / coral.
 * Reacts to the pointer and to `pulse()` calls (answers, completion).
 * Reduced motion: one static frame. No WebGL: a CSS gradient fallback.
 */

const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`

const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform vec2 uPointer;
uniform float uPulse;
uniform float uWarm;
uniform float uIntensity;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = p * 2.02 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float aspect = uRes.x / uRes.y;
  vec2 p = vec2(uv.x * aspect, uv.y);
  float t = uTime * 0.045;

  vec2 q = vec2(fbm(p * 1.6 + t), fbm(p * 1.6 - t * 0.7 + 3.1));
  vec2 r = vec2(fbm(p * 2.0 + q * 1.8 + t * 0.35), fbm(p * 2.0 + q * 1.8 - t * 0.25 + 7.3));
  float f = fbm(p * 1.7 + r * 1.5);

  vec2 pp = vec2(uPointer.x * aspect, uPointer.y);
  float d = length(p - pp);
  float glow = exp(-d * d * 7.0) * (0.09 + uPulse * 1.1);

  vec3 cobalt = vec3(0.20, 0.42, 1.00);
  vec3 violet = vec3(0.58, 0.30, 0.98);
  vec3 coral  = vec3(1.00, 0.46, 0.36);

  vec3 col = mix(cobalt, violet, smoothstep(0.38, 0.66, f));
  float warmMask = smoothstep(0.50, 0.80, r.x) * (0.30 + uWarm * 0.70);
  col = mix(col, coral, warmMask);

  // Ridged bands give the aurora visible structure instead of one soft blob.
  float bands = pow(smoothstep(0.30, 0.80, f), 1.6);
  float ridge = 1.0 - abs(fract(f * 3.0 + t) * 2.0 - 1.0);
  float lum = bands * 0.52 + ridge * bands * 0.22 + glow;
  vec3 base = vec3(0.040, 0.038, 0.070);
  vec3 outc = base + col * lum * uIntensity;

  float vig = smoothstep(1.25, 0.25, length(uv - 0.5) * 1.35);
  outc *= 0.62 + 0.38 * vig;

  gl_FragColor = vec4(outc, 1.0);
}
`

let pulseLevel = 0
let pulseTarget = { x: 0.72, y: 0.72 }

/** Send energy into the field. strength 0–1; position in 0–1 viewport coords (y up). */
export function pulse(strength = 0.6, at = null) {
  pulseLevel = Math.min(1.4, pulseLevel + strength)
  if (at) pulseTarget = { x: at.x, y: 1 - at.y }
}

function compile(gl, type, src) {
  const s = gl.createShader(type)
  gl.shaderSource(s, src)
  gl.compileShader(s)
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.warn(gl.getShaderInfoLog(s))
    return null
  }
  return s
}

function LightField({ intensity = 1, warm = 0 }) {
  const ref = useRef(null)
  const stateRef = useRef({ intensity, warm })
  stateRef.current = { intensity, warm }

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return undefined
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' })
    if (!gl) { canvas.classList.add('lightfield--fallback'); return undefined }

    const vs = compile(gl, gl.VERTEX_SHADER, VERT)
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG)
    if (!vs || !fs) { canvas.classList.add('lightfield--fallback'); return undefined }
    const prog = gl.createProgram()
    gl.attachShader(prog, vs)
    gl.attachShader(prog, fs)
    gl.linkProgram(prog)
    gl.useProgram(prog)

    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
    const aPos = gl.getAttribLocation(prog, 'aPos')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)

    const u = {
      res: gl.getUniformLocation(prog, 'uRes'),
      time: gl.getUniformLocation(prog, 'uTime'),
      pointer: gl.getUniformLocation(prog, 'uPointer'),
      pulse: gl.getUniformLocation(prog, 'uPulse'),
      warm: gl.getUniformLocation(prog, 'uWarm'),
      intensity: gl.getUniformLocation(prog, 'uIntensity'),
    }

    let w = 0
    let h = 0
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.25)
      // The field is soft; render at a fraction of the screen and let CSS upscale.
      w = Math.max(1, Math.floor(window.innerWidth * dpr * 0.5))
      h = Math.max(1, Math.floor(window.innerHeight * dpr * 0.5))
      canvas.width = w
      canvas.height = h
      gl.viewport(0, 0, w, h)
    }
    resize()
    window.addEventListener('resize', resize)

    const pointer = { x: 0.72, y: 0.72 }
    let hasPointer = false
    const onMove = (e) => {
      const p = e.touches ? e.touches[0] : e
      if (!p) return
      pulseTarget = { x: p.clientX / window.innerWidth, y: 1 - p.clientY / window.innerHeight }
      hasPointer = true
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('touchstart', onMove, { passive: true })

    let cur = { intensity: 0, warm: 0 }
    let raf = 0
    let running = true
    const start = performance.now()

    const frame = (now) => {
      if (!running) return
      const t = (now - start) / 1000
      const target = stateRef.current
      cur.intensity += (target.intensity - cur.intensity) * 0.04
      cur.warm += (target.warm - cur.warm) * 0.04
      const ease = hasPointer ? 0.06 : 0.02
      pointer.x += (pulseTarget.x - pointer.x) * ease
      pointer.y += (pulseTarget.y - pointer.y) * ease
      pulseLevel *= 0.955

      gl.uniform2f(u.res, w, h)
      gl.uniform1f(u.time, t)
      gl.uniform2f(u.pointer, pointer.x, pointer.y)
      gl.uniform1f(u.pulse, pulseLevel)
      gl.uniform1f(u.warm, cur.warm)
      gl.uniform1f(u.intensity, cur.intensity)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

      if (!reduce) raf = requestAnimationFrame(frame)
    }

    if (reduce) {
      cur = { intensity, warm }
      frame(start + 4000)
    } else {
      raf = requestAnimationFrame(frame)
    }

    const onVis = () => {
      if (document.hidden) { running = false; cancelAnimationFrame(raf) }
      else if (!reduce) { running = true; raf = requestAnimationFrame(frame) }
    }
    document.addEventListener('visibilitychange', onVis)

    return () => {
      running = false
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('touchstart', onMove)
      document.removeEventListener('visibilitychange', onVis)
      // Don't lose the context here: StrictMode remounts reuse the same canvas.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <canvas ref={ref} className="lightfield" aria-hidden="true" />
}

LightField.propTypes = {
  intensity: PropTypes.number,
  warm: PropTypes.number,
}

export default LightField
